import {
    ClaudeRun,
    ClaudeRunStatsResponse,
    ClaudeRunsResponse,
    GrantsResponse,
    RequestsFetchOpts,
    RequestsResponse,
    TempAccessRequest,
    TenantsResponse,
} from './api';
import {GrantView} from './grant-view';

/**
 * Mock grants used when `VITE_MOCK=1`. Lets us run the UI locally without OAuth or backend.
 * Picked to reproduce the search/filter cases we've been debugging:
 *   - "al"   → alex@ennabl.com only
 *   - "Pa"   → pavel@ennabl.com, pavle@ennabl.com, pa.shimanovich@ennabl.com
 *   - "alex" → alex@ennabl.com only
 */
const now = Date.now();
const iso = (offsetDays: number) => new Date(now + offsetDays * 86_400_000).toISOString();

const GRANTS: GrantView[] = [
    // Alex — permanent DataAccess (IAM) + Impersonation (AccessOps)
    {
        // IAM data-access grants arrive as the AWS account id, not the shard uid —
        // the chip dedup logic folds account id and shard uid to the same tenant.
        id: 'iam-bastion:alex.smith:DataAccessPolicy:092448557875',
        source: 'iam-bastion',
        email: 'alex@ennabl.com',
        resource: '092448557875',
        resourceName: 'Alera Group',
        kind: 'data-access',
        raw: {userName: 'alex.smith', policyName: 'DataAccessPolicy'},
    },
    {
        id: 'access-ops:impersonation:alex@ennabl.com:b6e5bf',
        source: 'access-ops',
        email: 'alex@ennabl.com',
        resource: 'b6e5bf',
        resourceName: 'Alera Group',
        kind: 'imp-decisions',
        raw: {grantedVia: 'access-ops'},
    },

    // Pavel
    {
        id: 'iam-bastion:pavel:DataAccessPolicy:092448557875',
        source: 'iam-bastion',
        email: 'pavel@ennabl.com',
        resource: '092448557875',
        resourceName: 'Alera Group',
        kind: 'data-access',
        raw: {userName: 'pavel', policyName: 'DataAccessPolicy'},
    },
    {
        id: 'iam-bastion:pavel:DataAccessPolicy:013611968594',
        source: 'iam-bastion',
        email: 'pavel@ennabl.com',
        resource: '013611968594',
        resourceName: 'Arlington Roe',
        kind: 'data-access',
        raw: {userName: 'pavel', policyName: 'DataAccessPolicy'},
    },

    // Pavle — all-tenants permanent DataAccess (wildcard DataAccessPolicy)
    {
        id: 'iam-bastion:pavle:DataAccessPolicy:*',
        source: 'iam-bastion',
        email: 'pavle@ennabl.com',
        resource: '*',
        kind: 'data-access',
        raw: {userName: 'pavle', policyName: 'DataAccessPolicy'},
    },

    // Pa.shimanovich — long localpart with dot. Tests the "unknown tenant" path:
    // resource is set but resourceName is omitted, so chips fall back to the id.
    {
        id: 'iam-bastion:pa.shimanovich:TemporaryDataAccess-1234567890:bxnknwn',
        source: 'iam-bastion',
        email: 'pa.shimanovich@ennabl.com',
        resource: 'bxnknwn',
        kind: 'data-access-temporary',
        expiresAt: iso(3),
        raw: {userName: 'pa.shimanovich', policyName: 'TemporaryDataAccess-1234567890'},
    },

    // Other users for noise
    {
        id: 'iam-bastion:carol:DataAccessPolicy:092448557875',
        source: 'iam-bastion',
        email: 'carol@ennabl.com',
        resource: '092448557875',
        resourceName: 'Alera Group',
        kind: 'data-access',
        raw: {userName: 'carol', policyName: 'DataAccessPolicy'},
    },
    {
        id: 'iam-bastion:dan:DataAccessPolicy:017263836104',
        source: 'iam-bastion',
        email: 'dan@ennabl.com',
        resource: '017263836104',
        resourceName: 'J. Byrne',
        kind: 'data-access',
        raw: {userName: 'dan', policyName: 'DataAccessPolicy'},
    },
    {
        id: 'access-ops:impersonation:eve@ennabl.com:b6e5bf',
        source: 'access-ops',
        email: 'eve@ennabl.com',
        resource: 'b6e5bf',
        resourceName: 'Alera Group',
        kind: 'imp-decisions',
        raw: {grantedVia: 'access-ops'},
    },
    // Ennabl AI impersonation — the new capability. Alex holds BOTH applications
    // on the same tenant, which must render as two separate chips.
    {
        id: 'access-ops:imp-ai:alex@ennabl.com:b6e5bf',
        source: 'access-ops',
        email: 'alex@ennabl.com',
        resource: 'b6e5bf',
        resourceName: 'Alera Group',
        kind: 'imp-ai',
        raw: {grantedVia: 'access-ops'},
    },
    // Two wildcards for one person, one per application: they must stay distinct
    // and neither may appear on the other's tab.
    {
        id: 'access-ops:imp-ai:pavle@ennabl.com:all',
        source: 'access-ops',
        email: 'pavle@ennabl.com',
        resource: '*',
        kind: 'imp-ai',
        raw: {grantedVia: 'access-ops'},
    },
    {
        id: 'access-ops:impersonation:pavle@ennabl.com:all',
        source: 'access-ops',
        email: 'pavle@ennabl.com',
        resource: '*',
        kind: 'imp-decisions',
        raw: {grantedVia: 'access-ops'},
    },
    // Time-boxed Ennabl AI grant — imp-ai allows expiry presets (Decisions does not),
    // so this is the fixture that exercises the amber/red badge on a teal chip.
    {
        id: 'access-ops:imp-ai:eve@ennabl.com:all',
        source: 'access-ops',
        email: 'eve@ennabl.com',
        resource: '*',
        kind: 'imp-ai',
        expiresAt: iso(5),
        raw: {grantedVia: 'access-ops'},
    },
    // A BROKEN subject: a shard uid where a subject should be. Unreachable through
    // the UI or the API (both validate), so this fixture stands in for a row that
    // got in around them — a hand-written DynamoDB put or a bad migration. It
    // authorizes nothing; the point is that it renders visibly broken so someone
    // finds and revokes it.
    {
        id: 'access-ops:imp-ai:carol@ennabl.com:b6e5bf',
        source: 'access-ops',
        email: 'carol@ennabl.com',
        resource: 'b6e5bf',
        kind: 'imp-ai',
        raw: {grantedVia: 'manual-ddb-write'},
    },
    // A domain-level subject — the middle level, and the one worth spotting.
    {
        id: 'access-ops:imp-ai:dan@ennabl.com:acme.com',
        source: 'access-ops',
        email: 'dan@ennabl.com',
        resource: 'acme.com',
        kind: 'imp-ai',
        expiresAt: iso(20),
        raw: {grantedVia: 'access-ops'},
    },
    // An exact-address subject, time-boxed — the everyday shape a trusted user
    // may now self-approve.
    {
        id: 'access-ops:imp-ai:bob@ennabl.com:john.doe@acme.com',
        source: 'access-ops',
        email: 'bob@ennabl.com',
        resource: 'john.doe@acme.com',
        kind: 'imp-ai',
        expiresAt: iso(3),
        raw: {grantedVia: 'access-ops'},
    },
    // Data Access on the internal Ennabl AI accounts. These are AWS accounts, so
    // they live here and NOT under either impersonation kind; the chip picks up
    // the green internal tint and the alias instead of the raw id.
    {
        id: 'iam-bastion:alex.smith:DataAccessPolicy:550428926429',
        source: 'iam-bastion',
        email: 'alex@ennabl.com',
        resource: '550428926429',
        kind: 'data-access',
        raw: {userName: 'alex.smith', policyName: 'DataAccessPolicy'},
    },
    {
        id: 'iam-bastion:pavel:TemporaryDataAccess-1:306656769105',
        source: 'iam-bastion',
        email: 'pavel@ennabl.com',
        resource: '306656769105',
        kind: 'data-access-temporary',
        expiresAt: iso(20),
        raw: {userName: 'pavel', policyName: 'TemporaryDataAccess-1'},
    },
    {
        id: 'iam-bastion:bob.jones:DataAccessPolicy:unknown',
        source: 'iam-bastion',
        email: 'bob@ennabl.com',
        resource: '?',
        kind: 'data-access',
        raw: {userName: 'bob.jones', policyName: 'DataAccessPolicy'},
    },
];

export function mockGrantsResponse(): GrantsResponse {
    return {
        grants: GRANTS,
        errors: [],
        count: GRANTS.length,
        syncedAt: Date.now() - 120_000, // pretend last sync was 2 min ago
    };
}

/**
 * Full tenant catalog for VITE_MOCK=1. Deliberately a superset of the tenants
 * that appear in GRANTS — e.g. "Elliot" and "Hilb Group" have zero grants, so
 * they exercise the picker showing tenants with no existing access.
 */
export function mockTenantsResponse(): TenantsResponse {
    const tenants = [
        {id: 'b6e5bf', name: 'Alera Group', accountId: '092448557875'},
        {id: 'a1b2c3', name: 'Arlington Roe', accountId: '013611968594'},
        {id: 'e7f8a9', name: 'Elliot', accountId: '111122223333'},
        {id: 'h4g5j6', name: 'Hilb Group', accountId: '444455556666'},
        {id: 'k1l2m3', name: 'J. Byrne', accountId: '017263836104'},
        {id: 'bxnknwn', name: 'Unknown Shard', accountId: '777788889999'},
    ].sort((a, b) => a.name.localeCompare(b.name));
    return {tenants, count: tenants.length};
}

const REQUESTS: TempAccessRequest[] = [
    {
        kind: 'data-access',
        grantedAt: new Date(now - 1 * 3_600_000).toISOString(), // 1h ago
        username: 'alex.smith',
        shardId: 'b6e5bf',
        reason: 'Review users data for DU mapping',
        policyTimestamp: String(now - 1 * 3_600_000),
    },
    {
        kind: 'imp-decisions',
        grantedAt: new Date(now - 2 * 3_600_000).toISOString(), // 2h ago
        email: 'alex.smith@ennabl.com',
        grantedBy: 'admin@ennabl.com',
        shardId: 'b6e5bf',
        reason: 'Reviewing customer-reported issue end-to-end',
        policyTimestamp: String(now - 2 * 3_600_000),
    },
    {
        kind: 'data-access',
        grantedAt: new Date(now - 6 * 3_600_000).toISOString(), // 6h ago — within 24h
        username: 'alex.smith',
        shardId: 'b6e5bf',
        reason: 'Follow-up on DU mapping issue',
        policyTimestamp: String(now - 6 * 3_600_000),
    },
    {
        kind: 'data-access',
        grantedAt: new Date(now - 2 * 86_400_000).toISOString(),
        username: 'pavel',
        shardId: '013611968594',
        reason: 'Investigate stale alerts',
        policyTimestamp: String(now - 2 * 86_400_000),
    },
    {
        kind: 'imp-decisions',
        grantedAt: new Date(now - 3 * 86_400_000).toISOString(),
        email: 'pavel@ennabl.com',
        grantedBy: 'pavel@ennabl.com',
        shardId: '*',
        reason: 'All-tenants Impers for cross-shard investigation',
        policyTimestamp: String(now - 3 * 86_400_000),
    },
    {
        kind: 'data-access',
        grantedAt: new Date(now - 4 * 86_400_000 - 3600_000).toISOString(),
        username: 'pa.shimanovich',
        shardId: '017263836104',
        reason: "looking into Ross's data",
        policyTimestamp: String(now - 4 * 86_400_000 - 3600_000),
    },
    {
        kind: 'data-access',
        grantedAt: new Date(now - 7 * 86_400_000 - 7200_000).toISOString(),
        username: 'alex.smith',
        shardId: 'bxnknwn',
        reason: 'benchmarking classify results',
        policyTimestamp: String(now - 7 * 86_400_000 - 7200_000),
    },
    {
        kind: 'data-access',
        grantedAt: new Date(now - 14 * 86_400_000).toISOString(),
        username: 'dan',
        shardId: '013611968594',
        reason: 'Updating hawksoft unifier',
        policyTimestamp: String(now - 14 * 86_400_000),
    },
];

export async function mockRequestsResponse(opts: RequestsFetchOpts = {}): Promise<RequestsResponse> {
    // Pretend Insights polling latency so the UI's progress UI shows up locally.
    await new Promise(r => setTimeout(r, 600));
    const filtered = REQUESTS.filter(r => {
        const t = Date.parse(r.grantedAt);
        if (opts.since !== undefined && t < opts.since) {
            return false;
        }
        if (opts.until !== undefined && t > opts.until) {
            return false;
        }
        return true;
    }).slice(0, opts.limit ?? 1000);
    return {requests: filtered, count: filtered.length};
}

/**
 * Canned rows for the admin "Claude runs" page under `VITE_MOCK=1`. One of each status so
 * the pills, the duration column and the deny reason render without scripting.
 */
const CLAUDE_RUNS: ClaudeRun[] = [
    {
        id: 'order-started',
        status: 'spawned',
        environment: 'ennabl-infra (aws cloud)',
        scope: 'infra',
        hostingAccount: '863233358811',
        email: 'mike@ennabl.com',
        user: 'mike',
        sessionId: 'session_01Started',
        sessionUrl: 'https://claude.ai/code/session_01Started',
        repo: 'accounts-bootstrap',
        repoUrl: 'https://github.com/ennabl/accounts-bootstrap',
        revision: 'feat/vpc-endpoints',
        requestedAt: now - 42 * 60_000,
        taskArn: 'arn:aws:ecs:us-east-1:863233358811:task/claude-runners/1a2b3c4d5e6f7890abcdef1234567890',
        taskId: '1a2b3c4d5e6f7890abcdef1234567890',
        taskFamily: 'claude-infra-session-large',
        size: 'large',
        logGroup: '/claude-runners/sessions',
        logStream: 'session-large/runner/1a2b3c4d5e6f7890abcdef1234567890',
    },
    {
        id: 'order-started-std',
        status: 'spawned',
        environment: 'ennabl-infra (aws cloud)',
        scope: 'infra',
        hostingAccount: '863233358811',
        email: 'ivan@ennabl.com',
        user: 'ivan',
        sessionId: 'session_02Started',
        sessionUrl: 'https://claude.ai/code/session_02Started',
        repo: 'toolbox',
        revision: 'master',
        requestedAt: now - 5 * 3_600_000,
        taskId: '9f8e7d6c5b4a39281706f5e4d3c2b1a0',
        taskFamily: 'claude-infra-session-std',
        size: 'std',
        logGroup: '/claude-runners/sessions',
        logStream: 'session-std/runner/9f8e7d6c5b4a39281706f5e4d3c2b1a0',
    },
    {
        id: 'order-denied',
        status: 'denied',
        environment: 'ennabl-infra (aws cloud)',
        scope: 'infra',
        hostingAccount: '863233358811',
        email: 'pavel@ennabl.com',
        sessionId: 'session_03Denied',
        sessionUrl: 'https://claude.ai/code/session_03Denied',
        repo: 'infra-tools',
        requestedAt: now - 26 * 3_600_000,
        denyReason: 'pavel@… is not a member of IAM group GlobalAdmin in BASTION',
    },
    {
        id: 'order-failed',
        status: 'failed',
        environment: 'ennabl-infra (aws cloud)',
        scope: 'infra',
        hostingAccount: '863233358811',
        email: 'mike@ennabl.com',
        user: 'mike',
        sessionId: 'session_04Failed',
        sessionUrl: 'https://claude.ai/code/session_04Failed',
        repo: 'infra-iam',
        revision: 'master',
        requestedAt: now - 2 * 86_400_000,
        failureReason: 'run-task returned no task: Capacity is unavailable at this time',
        size: 'large',
    },
];

export async function mockClaudeRunsResponse(opts: RequestsFetchOpts = {}): Promise<ClaudeRunsResponse> {
    await new Promise(r => setTimeout(r, 600));
    const since = opts.since ?? now - 7 * 86_400_000;
    const until = opts.until ?? now;
    const runs = CLAUDE_RUNS.filter(r => r.requestedAt >= since && r.requestedAt <= until).slice(0, opts.limit ?? 500);
    return {runs, count: runs.length, since, until};
}

/** Canned session stats under `VITE_MOCK=1`: a finished turn, a session that has not finished one yet. */
export async function mockClaudeRunStatsResponse(opts: RequestsFetchOpts = {}): Promise<ClaudeRunStatsResponse> {
    await new Promise(r => setTimeout(r, 1200));
    const {runs} = await mockClaudeRunsResponse(opts);
    const stats: ClaudeRunStatsResponse['stats'] = {};
    if (runs.some(r => r.id === 'order-started')) {
        stats['order-started'] = {
            title: 'Add VPC endpoints for ECR and Logs to the claude-runners VPC and drop the public subnets',
            prompts: 4,
            activeFrom: new Date(now - 41 * 60_000).toISOString(),
            activeTo: new Date(now - 3 * 60_000).toISOString(),
            runtimeMs: 41 * 60_000,
            usage: [
                {
                    model: 'claude-opus-5-5',
                    speed: 'standard',
                    messages: 35,
                    input: 70,
                    output: 31_063,
                    cacheRead: 5_344_505,
                    cacheWrite5m: 0,
                    cacheWrite1h: 158_783,
                },
                {
                    model: 'claude-haiku-4-5-20251001',
                    speed: 'standard',
                    messages: 6,
                    input: 12_000,
                    output: 3_400,
                    cacheRead: 0,
                    cacheWrite5m: 0,
                    cacheWrite1h: 0,
                },
            ],
            claudeUsd: 2.989,
            unpricedModels: [],
            awsUsd: 0.161,
        };
    }
    if (runs.some(r => r.id === 'order-started-std')) {
        stats['order-started-std'] = {runtimeMs: 2 * 60_000, usage: [], unpricedModels: [], awsUsd: 0.004};
    }
    return {stats, count: Object.keys(stats).length};
}
